import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * MarkdownArticle — the single place blog markdown becomes styled HTML.
 * Used by the public post page AND the admin editor preview so what the
 * owner sees while writing is exactly what parents/coaches see live.
 *
 * react-markdown escapes raw HTML by default (no `rehype-raw`), so
 * pasted or AI-generated content cannot inject markup.
 */
const components = {
    h1: ({ children }) => <h2 className="font-display mt-10 text-2xl font-bold text-[#0a1628] sm:text-3xl">{children}</h2>,
    h2: ({ children }) => <h2 className="font-display mt-10 text-2xl font-bold text-[#0a1628]">{children}</h2>,
    h3: ({ children }) => <h3 className="font-display mt-7 text-xl font-bold text-[#0a1628]">{children}</h3>,
    h4: ({ children }) => <h4 className="mt-5 text-lg font-semibold text-[#0a1628]">{children}</h4>,
    p: ({ children }) => <p className="mt-4 text-base leading-relaxed text-[#1a3a5c]">{children}</p>,
    ul: ({ children }) => <ul className="mt-4 list-disc space-y-2 pl-6 text-[#1a3a5c]">{children}</ul>,
    ol: ({ children }) => <ol className="mt-4 list-decimal space-y-2 pl-6 text-[#1a3a5c]">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-[#0a1628]">{children}</strong>,
    a: ({ href, children }) => {
        const external = /^https?:\/\//i.test(href || '') && !/splashpad\.pro/i.test(href || '');
        return (
            <a
                href={href}
                className="font-medium text-[#0a1628] underline decoration-[#00d4ff] decoration-2 underline-offset-2 hover:text-[#1a3a5c]"
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
                {children}
            </a>
        );
    },
    blockquote: ({ children }) => (
        <blockquote className="mt-6 border-l-4 border-[#00d4ff] bg-[#f6fbff] px-5 py-3 italic text-[#1a3a5c]">{children}</blockquote>
    ),
    code: ({ inline, children }) =>
        inline ? (
            <code className="rounded bg-[#0a1628]/5 px-1.5 py-0.5 font-mono text-sm text-[#0a1628]">{children}</code>
        ) : (
            <pre className="mt-4 overflow-x-auto rounded-xl bg-[#0a1628] p-4 font-mono text-sm text-white"><code>{children}</code></pre>
        ),
    hr: () => <hr className="my-10 border-[#0a1628]/10" />,
    table: ({ children }) => (
        <div className="mt-6 overflow-x-auto"><table className="w-full text-left text-sm text-[#1a3a5c]">{children}</table></div>
    ),
    th: ({ children }) => <th className="border-b border-[#0a1628]/15 px-3 py-2 font-semibold text-[#0a1628]">{children}</th>,
    td: ({ children }) => <td className="border-b border-[#0a1628]/10 px-3 py-2">{children}</td>,
    img: ({ src, alt }) => <img src={src} alt={alt || ''} loading="lazy" className="mt-6 w-full rounded-2xl" />,
};

export const MarkdownArticle = ({ content, className = '' }) => (
    <div className={className} data-testid="markdown-article">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {content || ''}
        </ReactMarkdown>
    </div>
);
